// ** React Imports
import React, { useEffect, useState } from 'react'

// ** MUI Imports
import { Button, IconButton, Tooltip, Typography } from '@mui/material'
import { Grid } from "@mui/material";


// ** Child Imports
import Loader from '../common/loader-components/Loader';
import { AnalyticsWidgetAPIPayloadType, AnalyticsWidgetDataSourceType, AnalyticsWidgetType, EmbededLinkType, TableComparisonMethodType } from 'src/lib/types/dashboardAnalytics'
import { getComparisonDataForWidgets, parseWidgetData } from 'src/lib/utils/analyticsHelpers';
import { getAnalyticsWidgetData } from 'src/lib/core/api-methods';
import { useLocale } from 'src/lib/hooks/useLocal';
import Alerts from '../common/feedback-and-dialog/Alerts';
import { alertSeverites, analyticsConstants } from 'src/lib/utils/constants';
import { ArrowRight, Download, Info } from 'react-feather';
import router from 'next/router';
import { getDateTime, getRepsonseFromMultiSource } from 'src/lib/utils/helpers';
import { useFilter } from 'src/lib/contexts/FilterContext'
import { isSameDay } from 'date-fns';

interface AnalyticsWidgetContainerProps {
    widgetId: string | string[];
    adapter?: any
    widgetContent: JSX.Element;
    APIPayloadObject: AnalyticsWidgetAPIPayloadType[];
    title: string
    tooltip?: string
    reportPagePath?: string | undefined
    hasReportDownload?: boolean
    embededLinks?: EmbededLinkType[] | undefined
    sourceType?: AnalyticsWidgetDataSourceType;
    comparisonMethod?: TableComparisonMethodType;
    moreIsGood?: boolean;
    hasComparison?: boolean;
    comparisonColumnIndex?: number;
}

const AnalyticsWidgetContainer: React.FC<AnalyticsWidgetContainerProps> = ({
    widgetId,
    adapter, 
    widgetContent,
    APIPayloadObject,
    title,
    tooltip,
    reportPagePath,
    hasReportDownload=false,
    embededLinks,
    sourceType,
    comparisonMethod,
    moreIsGood,
    hasComparison,
    comparisonColumnIndex
}) => {

    // Hooks
    const { t } = useLocale();
    const { analyticsFilters } = useFilter();
    
    const [ finalAPIPayload, setFinalAPIPayload ] = useState<any>();
    const [ fetchedWidget, setFetchedWidget ] = useState<AnalyticsWidgetType>();
   
    const [ showLoader, setShowLoader ] = useState<boolean>(false);
    const [ openAlert, setOpenAlert ] = useState<string>('');
    const [ alertMessage, setAlertMessage ] = useState<string>('');
    const [ downloadTriggered, setDownloadTriggered ] = useState<boolean>(false);
 
    const [ fetchedCompareToWidget, setFetchedCompareToWidget ] =  useState<AnalyticsWidgetType>();

    useEffect(() => {
        if (typeof APIPayloadObject !== "undefined") {
            setFinalAPIPayload(APIPayloadObject);
        }
    }, [APIPayloadObject])

    useEffect(() => {
        if (typeof finalAPIPayload !== "undefined") {
            callGetAnalyticsWidgetData(finalAPIPayload);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [finalAPIPayload])

    const callGetAnalyticsWidgetData = async (APIPayload: AnalyticsWidgetAPIPayloadType[]) => {
        setShowLoader(true)
        try{
            const promises = APIPayload?.map((payloadObject: AnalyticsWidgetAPIPayloadType) => getAnalyticsWidgetData(payloadObject));
            const responses: any = await Promise.allSettled(promises);
           
            if (APIPayload?.length > 1) {
                setFetchedCompareToWidget({data: parseWidgetData(APIPayload[1]?.widgetType, sourceType === "multiple" ? getRepsonseFromMultiSource(responses?.[1]?.value) : responses?.[1]?.value?.data, adapter, sourceType), error: responses?.[1]?.value?.error}); 
            }
            setFetchedWidget({data: parseWidgetData(APIPayload[0]?.widgetType, sourceType === "multiple" ? getRepsonseFromMultiSource(responses?.[0]?.value) : responses?.[0]?.value?.data, adapter, sourceType), error: responses?.[0]?.value?.error});
            setShowLoader(false);
        }
        catch (e) {
            console.error(e)
            setShowLoader(false);
            handleAlerts(
                alertSeverites.SEVERITY_ERROR, 
                t("analytics_fetch_failed")
            );
        }
    }

    const triggerRefresh = (APIPayload: AnalyticsWidgetAPIPayloadType, compareToAPIPayload?: AnalyticsWidgetAPIPayloadType) => {
        setFinalAPIPayload([APIPayload, ...(Boolean(compareToAPIPayload) ? [compareToAPIPayload]: [])]);
    }

    const processFetchedData = () => {
        if (Boolean(APIPayloadObject.length > 1)) {  
            return getComparisonDataForWidgets(fetchedCompareToWidget?.data?.widgetData, fetchedWidget?.data?.widgetData, APIPayloadObject[1]?.widgetType ?? "chart", comparisonMethod, moreIsGood, comparisonColumnIndex);
        }
        return fetchedWidget?.data?.widgetData;
    }
   
    // ** Alert Handlers ** //
    const handleAlerts = (severity: string, message?: string) => {
        setOpenAlert(severity);
        message && setAlertMessage(message);
    }

    const handleCloseAlert = (_event: any, reason: any) => {
        if (reason === 'clickaway') {
        return
        }
        handleAlerts('');
    }
        

    return (
        <>
            <Grid 
                container
                gap={6}   
                style={{ 
                height: "100%",
                backgroundColor: "white", 
                borderRadius: '8px', 
                padding: "1rem 0.75rem", 
                border: "1px solid #F0F0F0",
                display: "flex",
                flexDirection: "column"
            }}>
                <Grid 
                    sx={{
                        display: "flex",
                        width: "100%",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <div
                        style={{ 
                            display: "flex",
                            alignItems: "center"
                         }}>
                        
                        { Boolean(tooltip) ? <Tooltip title={tooltip ?? ""} placement='top-start'>
                            <Typography variant='subtitle1' sx={{ lineHeight: '24px', fontWeight: 500, color: "text.header", px: '4px', textDecoration: "underline", textDecorationColor: "#E0E0E0", textDecorationStyle: "dashed", textUnderlineOffset: 3, ":hover": {
                                background: "#F5F5F5", borderRadius: '4px', transition: 'all 0.2s ease-in-out',  textDecoration: "none"
                            } }}>
                                {title}
                            </Typography>
                        </Tooltip> : <Typography variant='subtitle1' sx={{ lineHeight: '24px', fontWeight: 500, color: "text.header", textDecoration: "underline", textDecorationColor: "#E0E0E0", textDecorationStyle: "dashed", textUnderlineOffset: 3 }}>
                                {title}
                            </Typography> }
                    </div>

                    <div 
                        style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                        justifyContent: "end",
                        }}
                    >
                        { hasReportDownload ? 
                        <Button
                            id={`${widgetId}-download-report`}
                            variant='text'
                            size="small"
                            disabled={APIPayloadObject?.[0]?.widgetType === "table" ? !fetchedWidget?.data?.widgetData?.length : !fetchedWidget?.data?.widgetData?.[0]?.data?.length}
                            onClick={() => setDownloadTriggered(true)}
                        >
                            <Download id={`${widgetId}-download-report-icon`} style={{ transform: "scale(0.75)" }}/>
                            Download report
                        </Button> : <></> }
                        { reportPagePath ? <Button
                            id={`${widgetId}-view-report`}
                            variant='text'
                            size="small"
                            sx={{ padding: 0, color: "#01846C" }}
                            onClick={() => {
                                router.push(`/overview/${reportPagePath}`)
                            }}
                        >
                            View report
                            <ArrowRight id={`${widgetId}-view-report-icon`} style={{ width: '18px', height: '16px', strokeWidth: "2.5px", paddingLeft: '2px' }}/>
                        </Button> : <></> }
                    </div>    
                </Grid>

                { fetchedWidget?.error ?
                    <div 
                        style={{
                            width: "100%",
                            display: "flex",
                            gap: 4,
                            alignItems: "start",
                            background: "#91C1E31A",
                            border: "1px solid #91C1E3",
                            borderRadius: 8,
                            padding: "0.5rem"
                        }}
                    >
                        <Info color={"#407BA5"} style={{ height: '14px', marginTop: 2 }} />
                        <Typography variant="body2" sx={{ color: "#407BA5", fontSize: '12px' }}>
                            {fetchedWidget?.error}
                        </Typography>
                    </div>
                    : <></> }

                { fetchedWidget?.data?.lastUpdated && fetchedWidget?.data?.widgetId === analyticsConstants.FLO_SALES_WIDGET ? 
                        <div className='flex items-start'>
                        <Info color={"#6E6E6E"} style={{ height: '14px', marginTop: 2 }} />
                        <Typography variant="body2" sx={{ color: "#6E6E6E", fontSize: '12px' }}>
                            This module was last updated {Boolean(isSameDay(new Date(fetchedWidget?.data?.lastUpdated), new Date())) ? "today" : Boolean(isSameDay(new Date(fetchedWidget?.data?.lastUpdated), new Date(new Date().getMilliseconds() - 84600000))) ? "yesterday" :  `on ${getDateTime(new Date(fetchedWidget?.data?.lastUpdated), 'Asia/Kolkata')?.date}` } at {getDateTime(new Date(fetchedWidget?.data?.lastUpdated), 'Asia/Kolkata')?.time} IST
                        </Typography>
                    </div>
                    : <></> }
                { React.cloneElement(
                    widgetContent, 
                    {
                        embededLinks: embededLinks,
                        value: processFetchedData() ?? [], 
                        isLoading: showLoader, 

                        triggerRefresh: triggerRefresh, 
                        ...(hasReportDownload ? 
                            {triggerDownloadReport: setDownloadTriggered,
                            downloadTriggered: downloadTriggered} : {}),
                        ...((typeof moreIsGood !== "undefined") ? {moreIsGood: moreIsGood} : {}),
                        hasComparison: hasComparison && analyticsFilters.comparison === "previous"
                    }
                ) }
            </Grid>
           

            { openAlert.length ? <Alerts 
                open = { true }
                duration = { 5000 }
                closeHandler = { handleCloseAlert } 
                severity = { openAlert } 
                message = { alertMessage }
            /> : null } 

        </>
    )
}

export default AnalyticsWidgetContainer
